import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCheck, UserX, MapPin, Download, Calendar, Clock, Search, AlertTriangle, CalendarOff, Home, ClipboardEdit, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar as CalendarWidget } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDate, formatTime } from '@/lib/dateUtils';
import { useBranchFilter } from '@/components/BranchFilterContext';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { branch } = useBranchFilter();
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [calOpen, setCalOpen] = useState(false);
  const [pendingOverrides, setPendingOverrides] = useState([]);
  const [overrideLoading, setOverrideLoading] = useState('');

  const dateStr = date.toISOString().split('T')[0];

  useEffect(() => {
    fetchDashboard();
    fetchPendingOverrides();
  }, [dateStr, branch]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getDashboard(dateStr, branch);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingOverrides = async () => {
    try {
      const res = await adminAPI.getAttendanceOverridesPending();
      setPendingOverrides(res.data.overrides || []);
    } catch { /* silent */ }
  };

  const handleOverrideDecision = async (id, status) => {
    setOverrideLoading(`${id}-${status}`);
    try {
      await adminAPI.decideOverride(id, status);
      toast.success(`Override ${status}`);
      fetchPendingOverrides();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setOverrideLoading('');
    }
  };

  const handleExport = async () => {
    try {
      const res = await adminAPI.exportAttendance(dateStr);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${dateStr}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported!');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const filteredRecords = data?.records?.filter(r =>
    r.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.user_phone?.includes(search)
  ) || [];

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 data-testid="dashboard-title" className="text-2xl font-extrabold text-[#1a2529] tracking-tight">Daily Dashboard</h1>
            <p className="text-sm text-[#1a2529]/50 mt-0.5">Track your team's attendance</p>
          </div>
          <div className="flex items-center gap-2">
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button data-testid="date-picker-btn" variant="outline" className="h-10 gap-2 font-medium">
                  <Calendar size={15} />
                  {formatDate(date.toISOString())}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarWidget
                  mode="single"
                  selected={date}
                  onSelect={(d) => { if (d) { setDate(d); setCalOpen(false); } }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button data-testid="export-btn" onClick={handleExport} className="h-10 bg-[#026536] hover:bg-[#026536]/90 gap-2 font-medium">
              <Download size={15} /> Export
            </Button>
          </div>
        </div>

        {/* Stats */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="stats-grid">
            <StatCard icon={Users} label="Total Staff" value={data.total_employees} color="blue" />
            <StatCard icon={UserCheck} label="Present" value={data.checked_in} color="green" />
            <StatCard icon={UserX} label="Absent" value={data.absent} color="red" />
            <StatCard icon={CalendarOff} label="On Leave" value={data.on_leave || 0} color="purple" />
            <StatCard icon={Home} label="WFH" value={data.wfh || 0} color={data.wfh > 0 ? "purple" : "gray"} />
            <StatCard icon={MapPin} label="Zones" value={data.zones?.length || 0} color="amber" />
            <StatCard icon={AlertTriangle} label="Outside Zone" value={data.outside_zone || 0} color={data.outside_zone > 0 ? "red" : "gray"} />
          </div>
        )}

        {/* Pending RM Overrides */}
        {pendingOverrides.length > 0 && (
          <div data-testid="pending-overrides-section" className="bg-[#dea439]/5 border border-[#dea439]/20 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <ClipboardEdit size={16} className="text-[#dea439]" />
              <h3 className="font-bold text-sm text-[#1a2529]">Pending RM Overrides ({pendingOverrides.length})</h3>
            </div>
            <div className="space-y-2">
              {pendingOverrides.map(o => (
                <div key={o.id} data-testid={`admin-override-${o.id}`} className="bg-white rounded-xl p-3 border border-border/20">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-sm text-[#1a2529]">{o.employee_name}</p>
                      <p className="text-[10px] text-[#1a2529]/30">{o.employee_phone} | Date: {formatDate(o.date)}</p>
                    </div>
                    <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[10px]">By {o.rm_name}</Badge>
                  </div>
                  {o.check_in_time && <p className="text-[10px] text-[#1a2529]/50">Check-in: {o.check_in_time}</p>}
                  {o.check_out_time && <p className="text-[10px] text-[#1a2529]/50">Check-out: {o.check_out_time}</p>}
                  <p className="text-[11px] text-[#1a2529]/40 italic mt-1">"{o.reason}"</p>
                  <div className="flex gap-2 mt-3 pt-2 border-t border-border/10">
                    <Button
                      data-testid={`approve-override-${o.id}`}
                      size="sm"
                      onClick={() => handleOverrideDecision(o.id, 'approved')}
                      disabled={!!overrideLoading}
                      className="bg-[#026536] hover:bg-[#026536]/90 text-xs h-8 gap-1 font-semibold"
                    >
                      {overrideLoading === `${o.id}-approved` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                      Approve
                    </Button>
                    <Button
                      data-testid={`reject-override-${o.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleOverrideDecision(o.id, 'rejected')}
                      disabled={!!overrideLoading}
                      className="text-red-500 border-red-200 hover:bg-red-50 text-xs h-8 gap-1 font-semibold"
                    >
                      {overrideLoading === `${o.id}-rejected` ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30" size={16} />
          <Input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone..."
            className="pl-10 h-10 bg-white"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-border/40 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="py-12 text-center">
              <UserCheck className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
              <p className="font-medium text-[#1a2529]/50">No attendance records for this date</p>
            </div>
          ) : (
            <Table data-testid="attendance-table">
              <TableHeader>
                <TableRow className="bg-[#f4f4f5]/50">
                  <TableHead className="font-semibold">Employee</TableHead>
                  <TableHead className="font-semibold hidden sm:table-cell">Phone</TableHead>
                  <TableHead className="font-semibold">Time</TableHead>
                  <TableHead className="font-semibold hidden md:table-cell">Location</TableHead>
                  <TableHead className="font-semibold hidden lg:table-cell">Zone</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((r, idx) => (
                  <TableRow
                    key={r.id}
                    data-testid={`attendance-row-${idx}`}
                    className="cursor-pointer hover:bg-[#026536]/5 transition-colors"
                    onClick={() => navigate(`/admin/employee/${r.user_id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-[#026536]/10 flex items-center justify-center text-xs font-bold text-[#026536]">
                          {r.user_name?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="font-semibold text-sm text-[#1a2529]">{r.user_name}</p>
                            {r.is_outside_zone && (
                              <AlertTriangle size={13} className="text-red-500" title="Outside assigned zone" />
                            )}
                            {r.is_late && (
                              <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[9px] px-1.5 py-0">Late</Badge>
                            )}
                            {r.is_wfh && (
                              <Badge className="bg-purple-50 text-purple-600 border-0 text-[9px] px-1.5 py-0">WFH</Badge>
                            )}
                          </div>
                          <p className="text-xs text-[#1a2529]/40 sm:hidden">{r.user_phone}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-[#1a2529]/60">{r.user_phone}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Clock size={12} className="text-[#026536]" />
                        <span className="text-sm font-medium text-[#1a2529]">
                          {formatTime(r.timestamp)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <p className="text-xs text-[#1a2529]/50 max-w-[200px] truncate">{r.address || `${r.latitude?.toFixed(4)}, ${r.longitude?.toFixed(4)}`}</p>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge className="bg-[#f4f4f5] text-[#1a2529]/60 border-0 text-xs">{r.zone_name || 'Unassigned'}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-[#026536]/10 text-[#026536]',
    red: 'bg-red-50 text-red-500',
    amber: 'bg-[#dea439]/10 text-[#dea439]',
    purple: 'bg-purple-50 text-purple-600',
    gray: 'bg-gray-100 text-gray-500',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-border/40 shadow-[0_2px_10px_-2px_rgba(0,0,0,0.05)]">
      <div className={`h-9 w-9 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon size={18} />
      </div>
      <p className="text-2xl font-extrabold text-[#1a2529]">{value}</p>
      <p className="text-xs text-[#1a2529]/40 font-medium mt-0.5">{label}</p>
    </div>
  );
}
